import React, { useContext } from 'react';
import { Breakpoint } from '../../BaseComponents/ThemeProvider/AvenyTheme';

export const BreakPoints = ['xs', 'sm', 'md', 'lg', 'xl'];
/** Config för värde som ska kunna variera per breakpoint, motsvarande hur de kan anges i en sx-prop.
 * Om en array anges så är det i ordningen xs, sm, md, lg, xl, om ett värde saknas så används det föregående värdet.
 * Om en objekt anges så anger man breakpoints för de värden som ska har nytt värde.
 * Man kan också ange ett värde som gäller för alla breakpoints.
 * Om ett värde för xs saknas så är resultatet undefined för alla värden till det första breakpointet som har ett värde.
 *
 */
export type Breakpointsconfig<T> =
  | { [key in Breakpoint]?: T }
  | [
      xs: T | undefined,
      sm: T | undefined,
      md: T | undefined,
      lg: T | undefined,
      xl: T | undefined
    ]
  | T;

/** Hitta värde för aktuell breakpoint i en BreakpointsConfig  */
export const findBreakpointValue = <T>(
  breakPointsConfig: Breakpointsconfig<T>,
  breakpoint: Breakpoint | undefined
): T | undefined => {
  if (breakpoint == undefined) return undefined;
  if (breakPointsConfig == undefined) return breakPointsConfig;
  if (typeof breakPointsConfig === 'object') {
    const index = BreakPoints.findIndex(v => v === breakpoint);
    for (let i = index; i > -1; i--) {
      const value = Array.isArray(breakPointsConfig)
        ? breakPointsConfig[i]
        : (breakPointsConfig as { [key in Breakpoint]?: T })[
            BreakPoints[i] as Breakpoint
          ];
      if (value) {
        return value;
      }
    }
    return undefined;
  }
  return breakPointsConfig;
};

export const compareBreakPoints = (
  width1: Breakpoint,
  width2: Breakpoint
): number =>
  BreakPoints.findIndex(v => v === width1) -
  BreakPoints.findIndex(v => v === width2);

export const breakpointIsLargerOrEqual = (
  bp: Breakpoint,
  currentValue: Breakpoint
): boolean => {
  const compareVal = compareBreakPoints(bp, currentValue);
  return compareVal <= 0;
};

/** Returns true if the breakpoint set is largethan the current screenwidth  */
export const breakpointIsLarger = (
  bp: Breakpoint,
  currentValue: Breakpoint
): boolean => {
  const compareVal = compareBreakPoints(bp, currentValue);
  return compareVal < 0;
};

export const breakpointIsSmallerOrEqual = (
  bp: Breakpoint,
  currentValue: Breakpoint
): boolean => {
  const compareVal = compareBreakPoints(bp, currentValue);
  return compareVal >= 0;
};

export const breakpointIsSmaller = (
  bp: Breakpoint,
  currentValue: Breakpoint
): boolean => {
  const compareVal = compareBreakPoints(bp, currentValue);
  return compareVal > 0;
};
export interface IBreakpointContext {
  /** Breakpoint för applikationen */
  globalBreakPoint: Breakpoint;
  /** Breakpoint för den aktuella delen av programmet, kompenserar för att en komponent på t.ex. högerläge bara för en mindre del av skärmen  */
  localBreakPoint: Breakpoint;
  /** Sant om primär pekinmatning är coarse  (oftast touch, media query: pointer: coarse) */
  coarsePointer: boolean;
}
const defaultValue: IBreakpointContext = {
  globalBreakPoint: 'md',
  localBreakPoint: 'md',
  coarsePointer: false,
};

export const BreakpointContext = React.createContext(defaultValue);

export const BreakpointContextProvider: React.Provider<IBreakpointContext> =
  BreakpointContext.Provider;

export const useBreakpointContext = () => useContext(BreakpointContext);

export const useWidth = () => {
  const breakpointContext = useBreakpointContext();
  return breakpointContext.localBreakPoint;
};

export const useIsBreakpoint = (breakpoint: Breakpoint): boolean => {
  const breakpointContext = useBreakpointContext();
  return breakpointContext.localBreakPoint === breakpoint;
};

export const useIsBreakpointOrLarger = (breakpoint: Breakpoint): boolean => {
  const breakpointContext = useBreakpointContext();
  return breakpointIsLargerOrEqual(
    breakpoint,
    breakpointContext.localBreakPoint
  );
};

export const useIsBreakpointOrSmaller = (breakpoint: Breakpoint): boolean => {
  const breakpointContext = useBreakpointContext();
  return breakpointIsSmallerOrEqual(
    breakpoint,
    breakpointContext.localBreakPoint
  );
};
export const useIsMediumBreakpoint = () => {
  const context = useBreakpointContext();
  return context.localBreakPoint === 'md';
};
export const useIsSmallBreakpoint = () => {
  const context = useBreakpointContext();
  return context.localBreakPoint === 'xs' || context.localBreakPoint === 'sm';
};

/** Om vi ska optimera för touch-enheter.
 * Baserat på att vi antingen har en xs breakpoint eller coarse pointer (touch)
 * En coarse pointer kan även vara vissa andra enheter där det inte möjligt att ha lika hög precision som med en mus.
 * Vi har valt att ta med även xs breakpoint dels för att kunna fortsätt använda cypress test som testar mobilvy genom att ställa in en liten viewport,
 * dels för att det är ovanligt att ha en så liten viewport på desktop så vi kan låta desktop layouter utgå från att det är minst en sm breakpoint.
 */
export const useIsTouchDevice = (): boolean => {
  const context = useBreakpointContext();
  return context.localBreakPoint === 'xs' || context.coarsePointer;
};
